<script setup>
import { useField, useFormContext } from 'vee-validate'
import { useTemplateRef, nextTick, watch, computed } from 'vue'
import { useRouter } from 'vue-router'

import ImoSpinner from '@/components/loading/ImoSpinner.vue'
import { useFormatTools } from '@/composables/useFormatTools'
import { useSearchDropdown } from '@/composables/useSearchDropdown'
import Contacts from '@/services/models/Contacts.js'
import { useAuthStore } from '@/stores/Auth.js'

const emit = defineEmits(['input', 'selectData'])

const { formatDocument, formatPhone } = useFormatTools()

const props = defineProps({
  size: {
    type: String,
    default: 'md',
    validator: value => ['sm', 'md', 'lg'].includes(value),
  },
  name: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    default: 'Contato:',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  onlyPrincipal: {
    type: String,
    default: null,
  },
  valueType: {
    type: String,
    default: 'id',
    validators: value => ['id', 'document'].includes(value),
  },
  showContactsByStatus: {
    type: [Boolean, null],
    default: null,
  },
})

const router = useRouter()
const authStore = useAuthStore()

const inputRef = useTemplateRef('inputRef')
const dropdownRef = useTemplateRef('dropdownRef')
const ulRef = useTemplateRef('ulRef')

const model = defineModel({ type: String })
const documentModel = defineModel('document', { type: String })

const { value, errorMessage, handleReset, setErrors } = useField(() => props.name, undefined, {
  syncVModel: true,
  validateOnValueUpdate: false,
  modelName: props.valueType === 'id' ? 'modelValue' : 'document',
})

const showOwnerSpan = computed(() => authStore.isAdmin)

// Acessa o schema do formulário pai via useFormContext
const { schema } = useFormContext()

// Usa o describe() do Yup para verificar se o campo tem a regra required
const isRequired = computed(() => {
  try {
    const fieldSchema = schema.value?.describe?.()?.fields?.[props.name]
    return fieldSchema?.tests?.some(t => t.name === 'required') ?? false
  } catch {
    return false
  }
})

// Hook do dropdown com lógica customizada
const {
  searchQuery,
  results,
  isDropdownVisible,
  isLoading,
  isFinished,
  activeIndex,
  isKeyboardNavigation,
  isInitializing,
  isSelecting,
  openDropdown,
  closeDropdown,
  selectResult,
  cleanAll,
  handleKeyDown,
  handleScroll,
  handleMouseEnterDropdown,
  handleMouseLeaveDropdown,
  handleMouseEnterItem,
} = useSearchDropdown({
  fetchService: url => Contacts.list(url),
  readService: id => Contacts.read(id),
  extractItem: resp => resp.data.contact,
  buildSearchUrl: searchValue => {
    let searchParam = searchValue
    const withoutFormatting = searchValue.replace(/[.\-/]/g, '')
    if (/^\d+$/.test(withoutFormatting)) {
      searchParam = withoutFormatting
    }

    return props.onlyPrincipal
      ? `?search=${searchParam}&responsible__isnull=${props.onlyPrincipal}&active=${props.showContactsByStatus}`
      : `?search=${searchParam}&active=${props.showContactsByStatus}`
  },
  onSelect: item => {
    handleReset()
    emit('selectData', item)
    value.value = item[props.valueType]
    searchQuery.value = item.name
  },
  inputRef,
  dropdownRef,
  ulRef,
  initialValue: computed(() => (props.valueType === 'id' ? model.value : documentModel.value)),
})

// Blur customizado para contactInput
const handleInputBlur = () => {
  setTimeout(() => {
    if (searchQuery.value && !value.value) {
      if (results.value.length > 0) {
        searchQuery.value = ''
        nextTick(() => {
          if (!value.value && !errorMessage.value) {
            setErrors('Por favor, selecione um contato da lista')
          }
        })
      }
    }
    closeDropdown()
  }, 200)
}

// Paste com debounce curto
const handlePaste = () => {
  nextTick(() => {
    openDropdown()
  })
}

// Override do cleanAll para incluir vee-validate
const handleCleanAll = () => {
  value.value = ''
  handleReset()
  cleanAll()
}

const handleOpenDropdown = () => {
  if (errorMessage.value) {
    handleReset()
  }
  openDropdown()
}

const newContact = () => {
  router.push('/contacts/create')
}

// Limpa valor quando usuário digita
watch(searchQuery, (newValue, oldValue) => {
  if (isSelecting.value) return

  // Limpa valor selecionado quando usuário digita
  if (!isInitializing.value && oldValue !== undefined && newValue !== oldValue && value.value) {
    value.value = ''
    handleReset()
  }
})

// Sincronizar v-model externo
watch(model, newValue => {
  if (!newValue) {
    searchQuery.value = ''
  }
})
</script>

<template>
  <div class="relative flex flex-col gap-1">
    <div class="flex items-center gap-2">
      <label
        class="text-neutral-60"
        :class="{
          'text-base': size === 'lg',
          'text-sm': size === 'md',
          'text-xs': size === 'sm',
        }"
        :for="$attrs.id"
      >
        {{ label }}
        <span v-if="isRequired" class="text-error-60">*</span>
      </label>
    </div>

    <div class="relative w-full">
      <input
        class="placeholder:text-neutral-40 w-full rounded-lg pr-16"
        :class="{
          error: errorMessage || (!results.length && !isLoading),
          input: !errorMessage && (results.length || isLoading),
          'h-[38px]': size === 'md',
          'h-[44px]': size === 'lg',
          'h-[36px]': size === 'sm',
          'border-neutral-30': !value,
          'border-neutral-40': value,
        }"
        id="search-input"
        type="text"
        v-model="searchQuery"
        @focus="handleOpenDropdown"
        @blur="handleInputBlur"
        @keydown="handleKeyDown"
        @paste="handlePaste"
        placeholder="Digite para buscar..."
        ref="inputRef"
        autocomplete="off"
      />

      <!-- Ícone de limpar/erro (X) ou spinner de loading -->
      <div v-if="searchQuery" class="absolute top-1/2 right-3 -translate-y-1/2">
        <ImoSpinner v-if="isLoading" :size="20" variant="tertiary" />
        <ImoIcon
          v-else
          @click="handleCleanAll"
          class="cursor-pointer"
          :class="{
            'text-error-60': errorMessage || (!results.length && !isLoading),
            'text-gray-400 hover:text-gray-600': !errorMessage && (results.length || isLoading),
          }"
          name="close"
        />
      </div>

      <!-- Ícone de seta (arrow-down) - aparece sempre que não há erro nem texto -->
      <ImoIcon
        v-if="!searchQuery"
        class="text-neutral-40 pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 transition-transform duration-200"
        :class="{
          'rotate-180': isDropdownVisible,
        }"
        name="arrow-down-02"
      />
    </div>
    <input id="client-input-id" type="text" v-model="value" class="hidden" />

    <!-- Dropdown -->
    <div
      v-if="isDropdownVisible"
      ref="dropdownRef"
      class="border-neutral-20 absolute top-17 z-40 w-full rounded-lg border bg-white p-1 shadow-lg"
      @mouseenter="handleMouseEnterDropdown"
      @mouseleave="handleMouseLeaveDropdown"
    >
      <div class="flex flex-col gap-0.5 rounded-lg">
        <ul
          v-if="results.length"
          ref="ulRef"
          class="dropdown-list scrollbar-thin scrollbar-track-transparent scrollbar-thumb-neutral-20 max-h-[215px] overflow-y-auto"
          @scroll="handleScroll"
        >
          <li
            v-for="(item, index) in results"
            :key="index"
            @mousedown="selectResult(item)"
            @mouseenter="handleMouseEnterItem(index)"
            class="flex cursor-pointer flex-col gap-0.5 rounded-lg py-2 pr-5 pl-4"
            :class="{
              'bg-neutral-20': index === activeIndex,
              'hover:bg-neutral-20': index !== activeIndex && !isKeyboardNavigation,
            }"
          >
            <span class="text-neutral-90 text-sm font-normal">{{ item.name }} </span>
            <span class="text-neutral-60 text-xs font-normal">
              {{ formatDocument(item.document) }}
              {{ item.phone ? `| ${formatPhone(item.phone)}` : '' }}
            </span>
            <span v-if="item.email" class="text-neutral-60 text-xs font-normal">
              {{ item.email }}
            </span>
            <span v-if="showOwnerSpan" class="text-neutral-60 text-xs font-normal">
              Dono: {{ item.owner_name }} ({{ formatDocument(item.owner_document) }})
            </span>
          </li>
          <!-- Spinner no final da lista quando carrega mais itens -->
          <li v-if="isLoading && !isFinished" class="flex justify-center py-3">
            <ImoSpinner :size="20" variant="tertiary" />
          </li>
        </ul>
        <div v-else-if="!isLoading" class="flex flex-col gap-6 rounded-lg py-2 pr-5 pl-4">
          <span class="text-error-60 text-center text-xs font-normal">
            Não localizado. Verifique os dados digitados ou cadastre um novo.
          </span>
        </div>
        <ImoTextButton
          text="Adicionar contato"
          size="sm"
          icon="add"
          iconPosition="left"
          @mousedown="newContact"
        />
      </div>
    </div>

    <!-- Erro para contato não encontrado -->
    <p
      v-if="!results.length && !isLoading && !errorMessage && searchQuery"
      class="text-error-60 text-xs"
    >
      Não localizado. Verifique os dados digitados ou
      <span class="cursor-pointer font-medium underline" @click="newContact"> cadastre um novo</span
      >.
    </p>

    <span class="text-error-60 text-xs" :id="`contact-${$attrs.id}-errors`" v-html="errorMessage" />
  </div>
</template>

<style scoped>
@reference "@/assets/style/main.css";

.input {
  @apply disabled:bg-neutral-10 disabled:border-neutral-30 disabled:text-neutral-40;
}

.input:focus-visible {
  @apply border-[#4c94ff73];
  box-shadow: 0px 0px 0px 3px #4c94ff73;
}

.error {
  @apply bg-error-10 border-error-60 text-error-60;
  box-shadow: 0px 0px 0px 3px #f008024d;
}
</style>
